import { useState, useEffect, useRef, useCallback } from "react";
import API_BASE_URL from "../config";
import { isDocumentTerminalStatus } from "../utils/documentStatus";

// Safety-net poll cadence — mirrors TakeoffWorkflow.jsx's STATUS_POLL_STARTING_MS.
const POLL_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_DOC_STREAM_POLL_MS) ||
      process.env.REACT_APP_DOC_STREAM_POLL_MS,
  ) || 3000;
const RECONNECT_DELAY_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_DOC_STREAM_RECONNECT_DELAY_MS) ||
      process.env.REACT_APP_DOC_STREAM_RECONNECT_DELAY_MS,
  ) || 3000;
const MAX_RECONNECT_ATTEMPTS =
  Number(
    (window._env_ && window._env_.REACT_APP_DOC_STREAM_MAX_RECONNECT_ATTEMPTS) ||
      process.env.REACT_APP_DOC_STREAM_MAX_RECONNECT_ATTEMPTS,
  ) || 3;

/**
 * Tracks one document's processing status. Primary transport is SSE
 * (`/api/v1/documents/{id}/stream`); an interval poll of the one-shot status
 * endpoint runs concurrently as a safety net (missed terminal event, dropped
 * connection past the reconnect budget) — mirrors TakeoffWorkflow.jsx's SSE +
 * safety-net-poll pattern.
 */
export default function useDocumentStatusStream(documentId, { enabled = true } = {}) {
  const [status, setStatus] = useState(null); // {status, message} | null
  const [connected, setConnected] = useState(false);
  const [streamKey, setStreamKey] = useState(0); // bumps to force EventSource reopen

  const terminalRef = useRef(false);
  const esRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);

  const applyStatus = useCallback((data) => {
    if (!data || !data.status) return;
    setStatus({ status: data.status, message: data.message || "" });
    if (isDocumentTerminalStatus(data.status)) terminalRef.current = true;
  }, []);

  // ── Safety-net poll ─────────────────────────────────────
  useEffect(() => {
    if (!enabled || !documentId) return undefined;
    terminalRef.current = false; // fresh processing cycle (also covers re-upload of the same id)
    let cancelled = false;
    let intervalId = null;

    const pollStatus = async () => {
      if (terminalRef.current || cancelled) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/documents/status/${documentId}`);
        if (cancelled) return;
        if (res.status >= 400 && res.status < 500) {
          // Client error (e.g. 404 — document doesn't exist): won't self-resolve by
          // retrying, so surface it as a terminal failure instead of spinning forever.
          applyStatus({ status: "failed", message: `Status check failed (${res.status})` });
          return;
        }
        if (!res.ok) return; // transient server error — retry on next tick
        const data = await res.json();
        if (!cancelled) applyStatus(data);
      } catch {
        // transient — keep polling, SSE (or the next tick) will recover
      }
    };

    pollStatus();
    intervalId = setInterval(() => {
      if (terminalRef.current) {
        clearInterval(intervalId);
        return;
      }
      pollStatus();
    }, POLL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [enabled, documentId, applyStatus]);

  // ── SSE stream (primary) ─────────────────────────────────
  useEffect(() => {
    if (!enabled || !documentId) return undefined;
    if (typeof window === "undefined" || !("EventSource" in window)) return undefined;

    const es = new EventSource(`${API_BASE_URL}/api/v1/documents/${documentId}/stream`, { withCredentials: true });
    esRef.current = es;

    es.onmessage = (e) => {
      let payload;
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      setConnected(true);
      if (payload.type === "heartbeat") return;
      reconnectAttemptsRef.current = 0; // any real message proves the connection is healthy
      if (payload.type === "status") {
        applyStatus(payload);
        if (isDocumentTerminalStatus(payload.status)) {
          es.close();
          esRef.current = null;
        }
      }
    };

    es.onerror = () => {
      setConnected(false);
      es.close();
      esRef.current = null;
      if (terminalRef.current) return; // already resolved — no need to reconnect

      reconnectAttemptsRef.current += 1;
      if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
        reconnectTimerRef.current = setTimeout(() => {
          setStreamKey((k) => k + 1);
        }, RECONNECT_DELAY_MS);
      }
      // Beyond MAX_RECONNECT_ATTEMPTS: rely entirely on the safety-net poll above.
    };

    return () => {
      es.close();
      esRef.current = null;
      setConnected(false);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [enabled, documentId, streamKey, applyStatus]);

  return { status, connected };
}
